import moment from 'jalali-moment';
import ReactPaginate from 'react-paginate';
import endpoints from '../../config/endpoints';
import globalEnum from '../../enums/globalEnum';
import useUrlParam from '../../hooks/useUrlParam';
import Message from '../../layouts/Modal/Message';
import { Sidebar } from "../../layouts/Sidebar";
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { popupSlice } from '../../services/reducers';
import { setLoading } from '../../services/reducers/homeSlice';
import { services } from '../../services/reducers/serviceSlice';
import restApi from '../../services/restApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from "sweetalert2";
import { useAppSelector } from '../../services/store';
import tools from '../../utils/tools';
import usePagination from "../../hooks/usePagination";

const UsersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<any>([]);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState(searchParams.get('tab') || 'all');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const serviceReducer = useAppSelector(state => state.serviceReducer);
  const { pageCount, endOffset, itemOffset, itemsPerPage, setPageCount, setItemOffset, setItemsPerPage } = usePagination(data);

  const tabTitles = {
    all: 'همه',
    USER: 'کاربران عادی',
    WORKER: 'زیباکار ها',
    OPERATOR: 'اپراتور ها',
    SUPER_ADMIN: 'ادمین ها',
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      const tablet = window.innerWidth <= 1024 && window.innerWidth > 768;
      setIsMobile(mobile);
      setIsTablet(tablet);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('.sideBarContainer');
      const toggle = document.querySelector('.mobile-menu-toggle');

      if (isMobile && sidebarOpen &&
        sidebar && !sidebar.contains(event.target as Node) &&
        toggle && !toggle.contains(event.target as Node)) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const downloadExcel = async () => {
    dispatch(setLoading(true));

    try {
      const res = await restApi(endpoints.user.excel).post({
        type: tab
      });

      if (res.data?.link) {
        window.open(res.data.link, '_blank');
        Swal.fire({
          title: 'موفق',
          text: 'فایل اکسل با موفقیت تولید شد',
          icon: 'success',
          confirmButtonText: 'متوجه شدم'
        });
      } else {
        Swal.fire({
          title: 'خطا',
          text: 'خطا در تولید فایل اکسل',
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'خطا',
        text: 'خطا در ارتباط با سرور',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const deleteItem = async (id: number) => {
    const result = await Swal.fire({
      title: 'آیا مطمئن هستید؟',
      text: 'این عمل قابل بازگشت نیست',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#c89dc7',
      confirmButtonText: 'بله، حذف کن',
      cancelButtonText: 'انصراف'
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(process.env.REACT_APP_BASE_URL + '/admin/user/' + id, true).delete({});

        if (res.code == 204) {
          Swal.fire({
            title: 'موفق',
            text: 'کاربر با موفقیت حذف شد',
            icon: 'success',
            confirmButtonText: 'متوجه شدم'
          });
          fetchData();
        } else {
          Swal.fire({
            title: 'ناموفق',
            text: res?.data || 'خطا در حذف کاربر',
            icon: 'error',
            confirmButtonText: 'متوجه شدم'
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'خطا',
          text: 'خطا در ارتباط با سرور',
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        });
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const changeStatus = async (id: number, status: number) => {
    const result = await Swal.fire({
      title: 'آیا مطمئن هستید؟',
      text: `آیا می‌خواهید این کاربر را ${status == 0 ? 'فعال' : 'غیرفعال'} کنید؟`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#c89dc7',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'بله',
      cancelButtonText: 'انصراف'
    });

    if (result.isConfirmed) {
      dispatch(setLoading(true));

      try {
        const res = await restApi(endpoints.user.active + id, true).post({
          status: status == 1 ? 0 : 1
        });

        if (res.code == 200) {
          Swal.fire({
            title: 'موفق',
            text: `کاربر با موفقیت ${status == 0 ? 'فعال' : 'غیرفعال'} شد`,
            icon: 'success',
            confirmButtonText: 'متوجه شدم'
          });
          fetchData();
        } else {
          Swal.fire({
            title: 'ناموفق',
            text: res?.data || 'خطا در تغییر وضعیت',
            icon: 'error',
            confirmButtonText: 'متوجه شدم'
          });
        }
      } catch (error) {
        Swal.fire({
          title: 'خطا',
          text: 'خطا در ارتباط با سرور',
          icon: 'error',
          confirmButtonText: 'متوجه شدم'
        });
      } finally {
        dispatch(setLoading(false));
      }
    }
  };

  const list = () => {
    const rows: any[] = [];

    data?.users?.map((user: any, index: number) => {
      const userServices: any = {};
      user.services?.map((e: any) => {
        const ancs = tools.findAncestors(serviceReducer.services, e.id)?.reverse();
        if (ancs && ancs.length) {
          userServices[ancs[0]?.title] = true;
        }
      });

      const servicesText = Object.keys(userServices).join(', ');
      const displayName = (user.name || '') + ' ' + (user.lastName || '');
      const lastEntrance = user.lastEntrance ? moment(user.lastEntrance).format('jYYYY/jMM/jDD') : '-';
      const codeOrServices = tab == 'WORKER' ? servicesText : user.tmpCode || '-';

      if (isMobile) {
        // Mobile card layout
        rows.push(
          <div key={`user-card-${index}`} className="user-card-mobile">
            <div className="user-card-header">
              <div className="user-info">
                <h3 className="user-name">{displayName}</h3>
                <p className="user-phone">{user.phoneNumber}</p>
              </div>
              <div className="user-actions">
                <button
                  className={`status-btn ${user.status == 1 ? 'active' : 'inactive'}`}
                  onClick={() => changeStatus(user.id, user.status)}
                  title={user.status == 1 ? 'غیرفعال کردن' : 'فعال کردن'}
                  aria-label={user.status == 1 ? 'غیرفعال کردن کاربر' : 'فعال کردن کاربر'}
                >
                  <i className={user.status == 1 ? 'activeUser' : 'deactiveUser'}></i>
                </button>
                <button
                  className="edit-btn"
                  onClick={() => navigate('/user/edit/' + user.id)}
                  title="ویرایش"
                  aria-label="ویرایش کاربر"
                >
                  <i className="edit"></i>
                </button>
                <button
                  className="delete-btn"
                  onClick={() => deleteItem(user.id)}
                  title="حذف"
                  aria-label="حذف کاربر"
                >
                  <i className="trash"></i>
                </button>
              </div>
            </div>

            <div className="user-card-body">
              <div className="user-detail">
                <span className="detail-label">آخرین ورود:</span>
                <span className="detail-value">{lastEntrance}</span>
              </div>

              <div className="user-detail">
                <span className="detail-label">
                  {tab == 'WORKER' ? 'لاین خدمت دهی:' : 'آخرین کد ورود:'}
                </span>
                <span className="detail-value">{codeOrServices}</span>
              </div>

              <div className="user-detail">
                <span className="detail-label">ردیف:</span>
                <span className="detail-value">
                  {((searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0) * itemsPerPage) + index + 1}
                </span>
              </div>
            </div>
          </div>
        );
      } else {
        // Desktop/Tablet table row
        rows.push(
          <tr className="dashTr2" key={`user-row-${index}`}>
            <td className="svgContainer">
              <i
                className={`${user.status == 1 ? 'activeUser' : 'deactiveUser'} clickable`}
                onClick={() => changeStatus(user.id, user.status)}
                title={user.status == 1 ? 'غیرفعال کردن' : 'فعال کردن'}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && changeStatus(user.id, user.status)}
              ></i>
              <i
                className="trash clickable"
                onClick={() => deleteItem(user.id)}
                title="حذف"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && deleteItem(user.id)}
              ></i>
              <i
                className="edit clickable"
                onClick={() => navigate('/user/edit/' + user.id)}
                title="ویرایش"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate('/user/edit/' + user.id)}
              ></i>
              <i
                className="messageIcon clickable iconSize"
                onClick={() => dispatch(popupSlice.middle(<Message user={user} />))}
                title="پیام"
                role="button"
                tabIndex={0}
              ></i>
            </td>
            <td>{lastEntrance}</td>
            <td>{codeOrServices}</td>
            <td>{displayName}</td>
            <td>{user.phoneNumber}</td>
            <td>{((searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0) * itemsPerPage) + index + 1}</td>
          </tr>
        );
      }
    });

    return rows;
  };

  const fetchData = async () => {
    !query && dispatch(setLoading(true));

    const params: any = {
      page: searchParams.get('page') || 1,
      perPage: itemsPerPage,
      query: query,
    };

    if (tab != 'all') {
      params.role = tab;
    }

    try {
      const res = await restApi(endpoints.user.index, true).get(params);

      setData({
        ...res.data,
        rolesCount: {...res.data.rolesCount,
          all: {
            title: 'همه',
            count: Object.values(res.data.rolesCount).reduce((acc, curr) => acc + ((curr as any).count || 0), 0)
          }
        }
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        title: 'خطا',
        text: 'خطا در دریافت لیست کاربران',
        icon: 'error',
        confirmButtonText: 'متوجه شدم'
      });
    } finally {
      !query && dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchParams.get('page'), itemsPerPage, query, tab]);

  useEffect(() => {
    if (data.count) {
      const newPage = Number(searchParams.get('page')) <= Math.ceil(data.count / itemsPerPage)
        ? searchParams.get('page') || '1'
        : '1';
      setSearchParams({
        ['page']: newPage,
        ['tab']: tab,
        ['query']: query
      });
    }
  }, [data]);

  return (
    <div className="dashboardBody">
      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={toggleSidebar}
          aria-label="منوی کناری"
          aria-expanded={sidebarOpen}
        >
          ☰
        </button>
      )}

      {/* Sidebar Backdrop */}
      {isMobile && sidebarOpen && (
        <div
          className={`sidebar-backdrop ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar className={`sideBarContainer ${sidebarOpen ? 'mobile-open' : ''}`} />

      <main className="dashBoardMain">
        <h1 className="dashBoardTitle">لیست کاربران</h1>

        <div className="dashTabs">
          {Object.entries(tabTitles).map(([key, value]) =>
            <span
              key={key}
              className={`ordersTag clickable ${key == tab ? 'activeTab' : ''}`}
              onClick={() => {
                setTab(key);
                setSearchParams({ ['page']: '1', ['tab']: key });
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setTab(key);
                  setSearchParams({ ['page']: '1', ['tab']: key });
                }
              }}
            >
              {value}
              {data?.rolesCount && (
                <span className={`numberTag ${key == tab ? 'activeTab' : ''}`}>
                  {data?.rolesCount[key]?.count || 0}
                </span>
              )}
            </span>
          )}
        </div>

        <div className="searchContainer">
          <span className="backSpan keepRight">
            <span
              className="dashboardHeader clickable"
              onClick={() => navigate('/user/add')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/user/add')}
            >
              <p>افزودن کاربر جدید</p>
              <i className="addPlus"></i>
            </span>

            <span
              className="dashboardHeader clickable"
              onClick={downloadExcel}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && downloadExcel()}
            >
              <p>خروجی اکسل</p>
              <i className="addPlus"></i>
            </span>
          </span>

          <div className='itemsPerPage'>
            <label htmlFor="itemPerPage">تعداد در صفحه</label>
            <input
              id='itemPerPage'
              type="number"
              min="1"
              max="100"
              defaultValue={itemsPerPage}
              onChange={(input) => {
                const value = Number(input.target.value);
                if (value && value > 0 && value <= 100) {
                  setItemsPerPage(value);
                }
              }}
              onBlur={(e) => {
                if (!e.target.value || Number(e.target.value) <= 0) {
                  e.target.value = itemsPerPage.toString();
                }
              }}
            />
          </div>

          <div className="dashboardseaechBox">
            <i className="dashMagnifierIcon"></i>
            <input
              className="dashSearchInput"
              placeholder="جستجو"
              value={query}
              onChange={(input) => setQuery(input.target.value)}
              aria-label="جستجوی کاربران"
            />
          </div>
        </div>
        <ReactPaginate
          breakLabel="..."
          nextLabel="بعدی >"
          onPageChange={(event) => {
            setSearchParams({['page']: (Number(event.selected) + 1).toString(), ['tab']: tab});
            setItemOffset((event.selected * itemsPerPage) % (data?.users?.length || 1));
          }}
          forcePage={Number(searchParams.get('page')) - 1}
          initialPage={searchParams.get('page') != null ? Number(searchParams.get('page')) - 1 : 0}
          pageRangeDisplayed={isMobile ? 3 : 5}
          pageCount={pageCount}
          previousLabel="< قبلی"
          renderOnZeroPageCount={null}
          className="pagination"
          pageClassName="paginationBreak"
          previousClassName="paginationBreak"
          nextClassName="paginationBreak"
          activeClassName="paginationActive"
        />
        {isMobile ? (
          // Mobile card layout
          <div className="users-mobile-container">
            {data?.users?.length > 0 ? (
              list()
            ) : (
              <div className="no-users-message">
                <p>هیچ کاربری یافت نشد</p>
              </div>
            )}
          </div>
        ) : (
          // Desktop/Tablet table layout
          <div className="table-container">
            <table>
              <thead>
              <tr className="dashTr1">
                <th>عملیات</th>
                <th>آخرین ورود</th>
                <th>{tab == 'WORKER' ? 'لاین خدمت دهی' : 'آخرین کد ورود'}</th>
                <th>نام و نام خانوادگی</th>
                <th>شماره موبایل</th>
                <th>ردیف</th>
              </tr>
              </thead>
              <tbody>
              {data?.users?.length > 0 ? (
                list()
              ) : (
                <tr>
                  <td colSpan={6} className="no-users-row">
                    هیچ کاربری یافت نشد
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>
        )}

        <ReactPaginate
          breakLabel="..."
          nextLabel="بعدی >"
          onPageChange={(event) => {
            setSearchParams({['page']: (Number(event.selected) + 1).toString(), ['tab']: tab});
            setItemOffset((event.selected * itemsPerPage) % (data?.users?.length || 1));
          }}
          forcePage={Number(searchParams.get('page')) - 1}
          initialPage={searchParams.get('page') != null ? Number(searchParams.get('page')) - 1 : 0}
          pageRangeDisplayed={isMobile ? 3 : 5}
          pageCount={pageCount}
          previousLabel="< قبلی"
          renderOnZeroPageCount={null}
          className="pagination"
          pageClassName="paginationBreak"
          previousClassName="paginationBreak"
          nextClassName="paginationBreak"
          activeClassName="paginationActive"
        />
      </main>
    </div>
  );
};

export default UsersList;